import { computed, signal } from '@preact/signals-react'
import type { AnalyticsBrowser } from '@segment/analytics-next'
import { getCookie, getOneTrustConsent } from '@susu/cookie'
import {
  getGA4ClientIdFromCookie,
  getGA4SessionDataFromCookie,
  setGA4MeasurementId,
} from '@susu/segment-common'
import { isNotUndefined } from '@susu/undefined'

import { createSegmentDestination, setAvoLoadedSignal } from './avo-destination'
import {
  EVENT_SEGMENT_LOADED,
  getGaCookieId,
  IDENTITY_KEY_ANONYMOUS,
  IDENTITY_KEY_KNOWN,
  IDENTITY_VALUE,
  LAST_PLATFORM_COOKIE_NAME,
  PLATFORM_COOKIE_NAME,
  setGaCookieId,
} from './constants'
import { isBrowser } from './env'
import { integrationPlugin } from './plugins'
import { analytics, initializeAnalytics } from './segment'
import type { InitializeSegmentOptions } from './types'
import {
  createPromiseFromSignal,
  errorHandler,
  getAvoEnv,
  getVersion,
  pushGA,
  setGALoadedSignal,
  validateApplicationId,
} from './utils'

// Export analytics instance for external use
export { analytics } from './segment'

// Export signals for external use
/** Signal indicating whether the Segment library has been loaded */
export const libraryLoaded = signal<boolean>(false)
/** Signal indicating whether Avo has been loaded */
export const avoLoaded = signal<boolean>(false)
/** Signal indicating whether Google Analytics has been loaded (undefined = not yet determined) */
export const gaLoaded = signal<boolean | undefined>(undefined)
/** Signal containing the AnalyticsBrowser instance once initialized */
export const library = signal<AnalyticsBrowser | undefined>(undefined)
/** Computed signal indicating whether both library and Avo are loaded */
export const loaded = computed(() => libraryLoaded.value && avoLoaded.value)

/** Promise that resolves when Google Analytics is loaded */
export const gaPromise = createPromiseFromSignal(gaLoaded)
/**
 * Promise that resolves when both Segment library and Avo are loaded.
 * This promise is created once at module load time and will resolve when the loaded computed signal becomes true.
 */
export const inspectorPromise = new Promise<boolean>(resolve => {
  if (loaded.value === true) {
    resolve(true)
    return
  }
  loaded.subscribe(value => {
    if (value === true) {
      resolve(true)
    }
  })
})

/**
 * Initializes Segment analytics with optional Avo integration.
 * This function should only be called once per application lifecycle.
 * It is safe to call in SSR environments and will only execute in the browser.
 *
 * @template AvoEnvType - The type of the Avo environment
 * @template ApplicationIdType - The type of the application ID
 * @param options - Configuration options for Segment initialization
 * @param options.applicationId - Required. Unique identifier for the application
 * @param options.userId - Optional. User ID for user identification
 * @param options.initAvo - Optional. Function to initialize Avo with Segment destination
 * @param options.version - Optional. Version string (defaults to NEXT_PUBLIC_APP_VERSION or '1.0.0')
 * @returns void
 * @remarks Initialization is non-fatal: any error during setup is logged via
 * {@link errorHandler} and swallowed so a tracking failure never breaks the host application.
 *
 * @example
 * ```typescript
 * initializeSegment({
 *   applicationId: 'my-app',
 *   userId: 'user-123',
 *   version: '2.0.0',
 *   initAvo: myAvoInitFunction,
 * })
 * ```
 */
export function initializeSegment<
  AvoEnvType = string,
  ApplicationIdType = string,
>({
  env,
  applicationId,
  userId,
  initAvo,
  version,
  useWithOneTrust = false,
  omitProperties,
}: InitializeSegmentOptions<AvoEnvType, ApplicationIdType>): void {
  // Early return for SSR or if already initialized
  if (!isBrowser() || isNotUndefined(library.value)) {
    return
  }

  // Validate required parameters. Initialization is non-fatal: log and bail out
  // instead of throwing so a misconfiguration never breaks the host application.
  try {
    validateApplicationId(applicationId)
  } catch (error) {
    errorHandler(error as Error)
    return
  }

  // Initialize analytics with write key from config. Bail out on failure rather
  // than continuing with an uninitialized analytics instance.
  try {
    initializeAnalytics(env.segmentWriteKey, useWithOneTrust)
  } catch (error) {
    errorHandler(error as Error)
    return
  }

  // Set GA cookie ID from config
  setGaCookieId(env.trackingGaCookie)
  if (env.trackingGaCookie) {
    setGA4MeasurementId(env.trackingGaCookie)
  }

  // Get validated version
  const resolvedVersion = getVersion(version, env.appVersion)

  const avoEnv = getAvoEnv(env.environmentType)

  setGALoadedSignal(gaLoaded)
  setAvoLoadedSignal(avoLoaded)

  const segmentDestination = createSegmentDestination()

  const onAnalyticsReady = (): void => {
    try {
      const consent = getOneTrustConsent()
      analytics.register(integrationPlugin({ omitProperties }))

      if (!isBrowser()) {
        return
      }

      const cookieId = getGaCookieId()
      const { sessionId, sessionNumber } = getGA4SessionDataFromCookie(
        cookieId ?? '',
      )
      const identifyTraits = {
        consent_onetrust_web: consent,
        ga_session_id: sessionId,
        ga_client_id: getGA4ClientIdFromCookie(),
        ga_session_number: sessionNumber,
      }

      // Identify user if userId is available
      if (userId && !window.sessionStorage.getItem(IDENTITY_KEY_KNOWN)) {
        window.sessionStorage.setItem(IDENTITY_KEY_KNOWN, IDENTITY_VALUE)
        analytics.identify(userId, identifyTraits)
      } else if (!window.sessionStorage.getItem(IDENTITY_KEY_ANONYMOUS)) {
        window.sessionStorage.setItem(IDENTITY_KEY_ANONYMOUS, IDENTITY_VALUE)
        analytics.identify(identifyTraits)
      }

      pushGA({ event: EVENT_SEGMENT_LOADED })

      library.value = analytics
      libraryLoaded.value = true
    } catch (error) {
      // Non-fatal: log and swallow so a tracking failure never breaks the host application.
      errorHandler(error as Error)
    }
  }

  analytics.ready(onAnalyticsReady)

  const setupAvo = (): void => {
    if (!initAvo) {
      return
    }

    try {
      if (typeof window === 'undefined') {
        return
      }

      const systemProperties = {
        applicationId: applicationId,
        version: resolvedVersion,
        clientId: getGA4ClientIdFromCookie(),
        sessionId: getGA4SessionDataFromCookie(getGaCookieId() ?? '').sessionId,
        platform: getCookie(PLATFORM_COOKIE_NAME) ?? '',
        lastPlatform: getCookie(LAST_PLATFORM_COOKIE_NAME) ?? '',
      }

      initAvo(
        {
          env: avoEnv as AvoEnvType,
        },
        systemProperties,
        {},
        segmentDestination as Parameters<typeof initAvo>[3],
      )

      avoLoaded.value = true
    } catch (error) {
      // Non-fatal: log and swallow so an Avo failure never breaks the host application.
      errorHandler(error as Error)
    }
  }

  if (initAvo) {
    setupAvo()
  }
}
